////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// drag/TokenDragController.ts — drag start for creature + inventory tokens (movement-mode resolution, outline).
// ----------------------------------------------------------------------------------------------------
// Phase 1.1c of the DragManager sub-controller extraction. The XZ-widget half (ghost / line setup, pickup and
// climb validation, grid coordinates, the frame-by-frame drag line + difficult-terrain overlay, the remote move
// preview) was RETIRED 2026-09-27 (Janus L2 S4 follow-up): the widget was deleted 2026-08-31 and every one of those
// paths required it, so none could run. FT3D owns world movement.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import type { DragManager as DragManagerInstance } from './DragManager';


// Host methods this controller calls back into. Extended as more methods are
// extracted across the three sub-phases of 1.1c.
type TokenDragHost = DragManagerInstance;


export class TokenDragController {
    constructor(private readonly host: TokenDragHost) {}


    // SOURCE: template.js DragManager._initDrag.
    // Initialize state for a new drag: flags, movement-mode resolution from speed type
    // (creature) or caster off-ground status (inventory), and visual outline / opacity
    // setup. The initiative-drag branch (combat strip) is detected here and the source
    // element gets `being-dragged` so it stays faded but in-place while the ghost flies.
    initDrag(_event: MouseEvent | TouchEvent, draggableEl: HTMLElement): void {
        const id = draggableEl.dataset.draggable_id;
        if (!id) throw new Error('Missing data-draggable_id');

        this.host.state.isDragging = true;
        // ft3d-drag-active reveals hidden corpses while a drag is in flight.
        document.body.classList.add('ft3d-drag-active');
        this.host.state.draggedElement = draggableEl;
        this.host.state.draggedItemId = id;

        // Defensive: wipe any stale OA threat / overrun decorations before the new drag starts.
        window.FT3D_OA_THREAT?.clear();
        window.FT3D_OVERRUN?.clear();

        // Dynamic movement mode based on element type.
        let movementMode = 'ground';

        if (
            draggableEl.classList.contains('encounter-creature')
            || draggableEl.classList.contains('gm-encounter-creature-card')
        ) {
            const preferredUuid = draggableEl.dataset.preferred_speed_type || '';

            if (preferredUuid && Object.prototype.hasOwnProperty.call(PREFERRED_SPEED_TO_MODE, preferredUuid)) {
                movementMode = PREFERRED_SPEED_TO_MODE[preferredUuid] ?? 'free';
            } else if (preferredUuid) {
                movementMode = 'free';
            }
        } else if (draggableEl.classList.contains('inventory-item')) {
            // If the item is already at an elevated Z on the map, drop free-form.
            // If it's resting on the ground (or has no map Z — side-panel drag),
            // default to ground-snap for scroll behavior.
            const itemHeightFt = parseFloat(draggableEl.dataset.height_ft || '') || 1;
            const curZ = parseFloat(draggableEl.dataset.z || '');
            const groundLockedZ = lockedGridZForMovementMode('ground', itemHeightFt, 5) ?? 0;
            const epsilon = 0.03;
            const elevated = Number.isFinite(curZ) && curZ > groundLockedZ + epsilon;

            movementMode = elevated ? 'free' : 'ground';
        } else {
            movementMode = 'free';
        }

        this.host.state.movementMode = movementMode;

        ftDebugLog('xz drag', { phase: 'movement mode', movementMode });

        draggableEl.style.outline = '4px dashed rgba(0, 255, 255, 0.9)';
        draggableEl.style.outlineOffset = '4px';
        draggableEl.style.transition = 'outline 0.2s ease';
    }


}
